import { z } from "zod"
import { MediaFileSchema, MediaFileType, type MediaFileData } from "@tutellus/schemas"

export const MediaFileValidation = z.object({
  id: z.string({ required_error: "id is required" }),
  keyS3: z.string({ required_error: "keyS3 is required" }),
  url: z.string({ required_error: "url is required" }).url(),
  type: z.nativeEnum(MediaFileType, { required_error: "type is required" }),
  name: z.string().optional(),
  size: z.number().optional(),
})

export class MediaFile {
  constructor(
    private readonly _id: string,
    private readonly _keyS3: string,
    private readonly _url: string,
    private readonly _type: MediaFileType,
    private readonly _name?: string,
    private readonly _size?: number,
  ) {}

  static create({ id, keyS3, url, type, name, size }: z.infer<typeof MediaFileValidation>) {
    const { error, success } = MediaFileValidation.safeParse({
      id,
      keyS3,
      url,
      type,
      name,
      size,
    })
    if (!success) throw new Error(error.message)

    return new MediaFile(id, keyS3, url, type, name, size)
  }

  static createFromAPI(data: MediaFileData) {
    const { error, success } = MediaFileSchema.safeParse(data)
    if (!success) throw new Error(`Invalid MediaFile data: ${error.message}`)

    return MediaFile.create({
      id: data.id,
      keyS3: data.keyS3,
      url: data.url,
      type: data.type,
      name: data.name ?? undefined,
      size: data.size ?? undefined,
    })
  }

  get id() { return this._id } // prettier-ignore
  get keyS3() { return this._keyS3 } // prettier-ignore
  get url() { return this._url } // prettier-ignore
  get type() { return this._type } // prettier-ignore
  get name() { return this._name } // prettier-ignore
  get size() { return this._size } // prettier-ignore

  get isVideo(): boolean {
    return this.type === MediaFileType.VIDEO_MP4 || this.type.startsWith("video/")
  }

  get isImage(): boolean {
    return this.type.startsWith("image/")
  }

  serialize() {
    return {
      id: this.id,
      name: this.name ?? undefined,
      type: this.type,
      size: this.size ?? undefined,
      keyS3: this.keyS3,
      url: this.url,
    }
  }
}

export type MediaFileSerialized = ReturnType<MediaFile["serialize"]>
